<template>
  <v-dialog v-model="dialog" persistent max-width="420px">
    <template v-slot:activator="{ on }">
      <v-btn v-on="on" small rounded outlined color="primary">
        <v-icon left small>mdi-lock-reset</v-icon>
        Change Password
      </v-btn>
    </template>

    <v-card class="password-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Change Password</div>
          <div class="dialog-subtitle">
            Update login password for this user.
          </div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div class="form-section">
          <div v-if="User" class="user-summary">
            <div class="user-avatar">
              {{ getInitial(User.name) }}
            </div>

            <div>
              <div class="user-name">
                {{ User.name || "User" }}
              </div>
              <div class="user-email">
                {{ User.email || "No email" }}
              </div>
            </div>
          </div>

          <div v-if="!canResetWithoutOldPassword" class="field-label mt-4">
            Old Password
          </div>

          <v-text-field
            v-if="!canResetWithoutOldPassword"
            v-model="oldPassword"
            outlined
            dense
            :hide-details="oldPasswordErrors.length === 0"
            :append-icon="
              showOldPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
            "
            :type="showOldPassword ? 'text' : 'password'"
            label="Old Password"
            prepend-inner-icon="mdi-lock-outline"
            :error-messages="oldPasswordErrors"
            @click:append="showOldPassword = !showOldPassword"
          />

          <div class="field-label mt-4">New Password</div>

          <v-text-field
            v-model="newPassword"
            outlined
            dense
            :hide-details="newPasswordErrors.length === 0"
            :append-icon="
              showNewPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
            "
            :type="showNewPassword ? 'text' : 'password'"
            label="New Password"
            prepend-inner-icon="mdi-lock-reset"
            :error-messages="newPasswordErrors"
            @click:append="showNewPassword = !showNewPassword"
            @keyup.enter="update"
          />
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />

        <v-btn color="primary" text @click="close"> Close </v-btn>

        <v-btn
          color="primary"
          depressed
          rounded
          dark
          :loading="loading"
          @click="update"
        >
          Update Password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import userService from "../service";
import { mapGetters } from "vuex";

const { validationMixin } = require("vuelidate");
const { required, minLength, requiredIf } = require("vuelidate/lib/validators");

export default {
  name: "change-password",

  props: ["UserId"],

  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      showOldPassword: false,
      showNewPassword: false,
      dialog: false,
      User: null,
      oldPassword: null,
      newPassword: null,
    };
  },

  validations() {
    return {
      oldPassword: {
        required: requiredIf(function () {
          return !this.canResetWithoutOldPassword;
        }),
        minLength: minLength(4),
      },
      newPassword: {
        required,
        minLength: minLength(4),
      },
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    canResetWithoutOldPassword() {
      if (!this.User) {
        return false;
      }

      if (this.currentUser.superAdmin) {
        return true;
      }

      if (this.currentUser.isAdmin && !this.User.superAdmin) {
        return true;
      }

      return false;
    },

    oldPasswordErrors() {
      const errors = [];

      if (!this.$v.oldPassword.$dirty) {
        return errors;
      }

      if (!this.$v.oldPassword.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.oldPassword.minLength) {
        errors.push(this.$t("validations.fieldMustbeatLeast4ChrLong"));
      }

      return errors;
    },

    newPasswordErrors() {
      const errors = [];

      if (!this.$v.newPassword.$dirty) {
        return errors;
      }

      if (!this.$v.newPassword.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.newPassword.minLength) {
        errors.push(this.$t("validations.fieldMustbeatLeast4ChrLong"));
      }

      return errors;
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.getUser();
      } else {
        this.resetForm();
      }
    },
  },

  methods: {
    getInitial(name) {
      if (!name) {
        return "?";
      }

      return name.charAt(0).toUpperCase();
    },

    resetForm() {
      this.User = null;
      this.oldPassword = null;
      this.newPassword = null;
      this.showOldPassword = false;
      this.showNewPassword = false;
      this.loading = false;

      if (this.$v) {
        this.$v.$reset();
      }
    },

    close() {
      this.dialog = false;
    },

    async getUser() {
      try {
        const response = await userService.getById(this.UserId);
        this.User = response.data;
      } catch (error) {
        const message =
          error && error.data && error.data.message
            ? error.data.message
            : "Unable to load user";

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: message,
          });

        this.close();
      }
    },

    async update() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (!this.User || !this.User.id) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "User was not loaded",
          });

        return;
      }

      this.loading = true;

      const data = {
        oldPassword: this.oldPassword,
        newPassword: this.newPassword,
      };

      if (this.currentUser.superAdmin) {
        data.superAdmin = true;
      }

      try {
        const result = await userService.changePassword(this.User.id, data);

        if (result.status) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Password changed!",
            });

          this.close();

          const isChangingOwnPassword =
            this.currentUser &&
            this.User &&
            parseInt(this.currentUser.id) === parseInt(this.User.id);

          if (isChangingOwnPassword && !this.canResetWithoutOldPassword) {
            this.$router.push("/logout");
            return;
          }

          this.$router.push("/user/list");
        }
      } catch (err) {
        const message =
          err && err.data && err.data.message
            ? err.data.message
            : "Unable to change password";

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: message,
          });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.password-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
}

.user-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.user-avatar {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.user-name {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.user-email {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  word-break: break-word;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}

@media (max-width: 600px) {
  .dialog-header {
    padding: 14px 16px;
  }

  .dialog-title {
    font-size: 18px;
  }

  .form-section {
    padding: 12px;
  }
}
</style>